import { useMemo } from 'react'
import { createEntityHooks } from '../core/createEntityHooks'
import { usePages } from './pages'
import { useProjects } from './projects'
import { useTasks } from './tasks'
import type { BaseDocument } from '../core/types'

/**
 * EntityLink document interface
 */
export interface EntityLinkDocument extends BaseDocument {
  sourceEntityType: string
  sourceEntityId: string
  targetEntityType: string
  targetEntityId: string
  linkType: number // 0 = inline, 1 = explicit
  sourceEntityName?: string
  sourceEntityIcon?: string | null
  targetEntityName?: string
  targetEntityIcon?: string | null
}

/**
 * Enriched entity link with resolved display names
 */
export interface EnrichedEntityLink extends EntityLinkDocument {
  resolvedTargetName: string
  resolvedTargetIcon: string | null
  resolvedTargetIconType: string | null
  resolvedTargetIconColor: string | null
  resolvedTargetIsArchived: boolean
  resolvedSourceName: string
  resolvedSourceIcon: string | null
  resolvedSourceIconType: string | null
  resolvedSourceIconColor: string | null
  resolvedSourceIsArchived: boolean
}

/**
 * EntityLink-specific hooks
 */
const hooks = createEntityHooks<EntityLinkDocument>('entitylinks')

/**
 * Build a lookup map from tasks, pages, and projects for name resolution.
 */
function useEntityNameMap() {
  const { tasks } = useTasks({ isDeleted: false })
  const { pages } = usePages()
  const { projects } = useProjects()

  return useMemo(() => {
    const map = new Map<
      string,
      {
        name: string
        icon: string | null
        iconType: string | null
        iconColor: string | null
        isArchived: boolean
      }
    >()
    for (const t of tasks) {
      map.set(t.id, {
        name: t.name || '',
        icon: (t as any).identifier ?? null,
        iconType: null,
        iconColor: null,
        isArchived: t.isArchived === true,
      })
    }
    for (const p of pages) {
      map.set(p.id, {
        name: p.title || '',
        icon: p.icon ?? null,
        iconType: p.iconType ?? null,
        iconColor: p.iconColor ?? null,
        isArchived: false,
      })
    }
    for (const pr of projects) {
      map.set(pr.id, {
        name: pr.name || '',
        icon: pr.icon ?? null,
        iconType: pr.iconType ?? null,
        iconColor: pr.iconColor ?? null,
        isArchived: false,
      })
    }
    return map
  }, [tasks, pages, projects])
}

/**
 * Get forward links: entities that THIS entity links TO.
 * Enriches with fresh names from local collections.
 */
export const useEntityLinks = (entityId: string | undefined) => {
  const { data: outgoing, isLoading } = hooks.useEntities(
    entityId ? { sourceEntityId: entityId, isDeleted: false } : undefined,
    { enabled: !!entityId },
  )
  const entityMap = useEntityNameMap()

  const links = useMemo<Array<EnrichedEntityLink>>(() => {
    if (!entityId) return []

    return outgoing.map((link) => {
      const resolved = entityMap.get(link.targetEntityId)
      return {
        ...link,
        resolvedTargetName: resolved?.name ?? link.targetEntityName ?? '',
        resolvedTargetIcon: resolved?.icon ?? link.targetEntityIcon ?? null,
        resolvedTargetIconType: resolved?.iconType ?? null,
        resolvedTargetIconColor: resolved?.iconColor ?? null,
        resolvedTargetIsArchived: resolved?.isArchived ?? false,
        resolvedSourceName: link.sourceEntityName ?? '',
        resolvedSourceIcon: link.sourceEntityIcon ?? null,
        resolvedSourceIconType: null,
        resolvedSourceIconColor: null,
        resolvedSourceIsArchived: false,
      }
    })
  }, [entityId, outgoing, entityMap])

  return { links, isLoading }
}

/**
 * Get backlinks: entities that link TO this entity.
 * Enriches with fresh names from local collections.
 */
export const useBacklinks = (entityId: string | undefined) => {
  const { data: incoming, isLoading } = hooks.useEntities(
    entityId ? { targetEntityId: entityId, isDeleted: false } : undefined,
    { enabled: !!entityId },
  )
  const entityMap = useEntityNameMap()

  const backlinks = useMemo<Array<EnrichedEntityLink>>(() => {
    if (!entityId) return []

    return incoming.map((link) => {
      const resolved = entityMap.get(link.sourceEntityId)
      return {
        ...link,
        resolvedSourceName: resolved?.name ?? link.sourceEntityName ?? '',
        resolvedSourceIcon: resolved?.icon ?? link.sourceEntityIcon ?? null,
        resolvedSourceIconType: resolved?.iconType ?? null,
        resolvedSourceIconColor: resolved?.iconColor ?? null,
        resolvedSourceIsArchived: resolved?.isArchived ?? false,
        resolvedTargetName: link.targetEntityName ?? '',
        resolvedTargetIcon: link.targetEntityIcon ?? null,
        resolvedTargetIconType: null,
        resolvedTargetIconColor: null,
        resolvedTargetIsArchived: false,
      }
    })
  }, [entityId, incoming, entityMap])

  return { backlinks, isLoading }
}

export const useEntityLink = (id: string | undefined) => {
  const { data, isLoading } = hooks.useEntity(id)
  return { entityLink: data, isLoading }
}

export const useEntityLinkMutations = () => {
  const { create, update, delete: deleteEntity } = hooks.useMutations()
  return {
    createEntityLink: create,
    updateEntityLink: update,
    deleteEntityLink: deleteEntity,
  }
}
